import apiClient from './api';
import { Finding } from '../types/api.types';

/**
 * Service for managing findings
 */
export class FindingService {
  private findingsUrl = '/pentest/ui/findings';

  /**
   * Get all findings for a project
   */
  async getFindings(projectId: string): Promise<Finding[]> {
    return apiClient.get<Finding[]>(`${this.findingsUrl}?project_id=${projectId}`);
  }

  /**
   * Create a new finding
   */
  async createFinding(finding: { 
    name: string; 
    description?: string; 
    severity: 'low' | 'medium' | 'high' | 'critical';
    project_id: string;
    is_manual?: boolean;
  }): Promise<Finding> {
    return apiClient.post<Finding>(this.findingsUrl, finding);
  }

  /**
   * Delete a finding
   */
  async deleteFinding(id: string): Promise<void> {
    return apiClient.delete<void>(`${this.findingsUrl}/${id}`);
  }

  /**
   * Upload a file to a finding
   */
  async uploadFindingFile(findingId: string, file: File, projectId: string): Promise<any> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('project_id', projectId);
    
    return apiClient.postForm<any>(`${this.findingsUrl}/${findingId}/file`, formData);
  }

  /**
   * Get the URL for a finding's file
   */
  getFindingFileUrl(findingId: string): string {
    return `${apiClient.getBaseUrl()}${this.findingsUrl}/${findingId}/file`;
  }

  /**
   * Get a finding's file content as text
   * This is useful for getting the content of report files
   */
  async getFindingFileContent(findingId: string): Promise<string> {
    try {
      const response = await apiClient.get<string>(`${this.findingsUrl}/${findingId}/file`, {
        responseType: 'text'
      });
      return response;
    } catch (error) {
      console.error('Error fetching finding file content:', error);
      throw error;
    }
  }
}

// Export singleton instance
export const findingService = new FindingService();
export default findingService;
